// Shared API + utilities + scenario/catalog data for BioForge AI frontend.
import { useEffect, useRef, useState, useCallback } from "react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Snake_case event name → *Agent class name + sidebar description + icon hint.
export const AGENTS = [
  { event: "problem_analyst", cls: "ProblemAnalysisAgent", short: "Problem", desc: "Disease burden, unmet needs, current solutions, opportunity gaps." },
  { event: "research_scientist", cls: "ResearchAgent", short: "Research", desc: "Biomarkers, pathways, existing diagnostics & therapeutics." },
  { event: "technology_matchmaker", cls: "TechnologySelectionAgent", short: "Tech Selection", desc: "Scores all 14 enabling technologies and picks top fits." },
  { event: "innovation_architect", cls: "InnovationArchitectAgent", short: "Innovation", desc: "Three concrete, differentiated venture concepts." },
  { event: "prototype_designer", cls: "PrototypeDesignerAgent", short: "Prototype", desc: "System architecture, BoM, fabrication & software stack." },
  { event: "validation_planner", cls: "ValidationPlannerAgent", short: "Validation", desc: "Analytical, technical & pilot validation plans." },
  { event: "regulatory_planner", cls: "RegulatoryPlannerAgent", short: "Regulatory", desc: "CDSCO / FDA / CE pathways and required documentation." },
  { event: "clinical_trial_designer", cls: "ClinicalTrialDesignerAgent", short: "Clinical Trial", desc: "Pivotal study protocol, endpoints, sample size, timeline." },
  { event: "commercialization_agent", cls: "CommercializationAgent", short: "Commercial", desc: "TAM/SAM/SOM, business model, partners, roadmap." },
  { event: "founder_review", cls: "FounderReviewAgent", short: "Founder Review", desc: "Critical review of the generated venture — risks & mitigations." },
  { event: "scorer", cls: "VentureScorerAgent", short: "Scorecard", desc: "Calibrated 8-axis venture scorecard with rationales." },
  { event: "final_report_compiler", cls: "FinalReportCompilerAgent", short: "Final Report", desc: "Company name, pitch deck digest, assembled venture report." },
];

export const AGENT_BY_EVENT = Object.fromEntries(AGENTS.map((a) => [a.event, a]));

export const DEMO_SCENARIOS = [
  {
    id: "sepsis",
    title: "Early Sepsis Diagnosis in Rural India",
    blurb: "Point-of-care sepsis triage at PHCs without reliable labs or power.",
    inputs: {
      problem: "Early sepsis diagnosis in rural India",
      population: "Patients in primary health centres with suspected infection",
      constraints: "Low-cost, point-of-care, minimal lab infrastructure, intermittent power",
      geography: "India (Tier 3 cities and rural)",
    },
  },
  {
    id: "amr",
    title: "Antimicrobial Resistance Surveillance",
    blurb: "Affordable AMR signal detection for under-resourced district hospitals.",
    inputs: {
      problem: "Affordable antimicrobial resistance surveillance for district hospitals",
      population: "Hospital inpatients receiving empiric antibiotics",
      constraints: "Limited microbiology lab capacity, no sequencing on-site, budget < $50k per site",
      geography: "South / Southeast Asia",
    },
  },
  {
    id: "ovarian",
    title: "Early Ovarian Cancer Detection",
    blurb: "A non-invasive screen that catches ovarian cancer before stage III.",
    inputs: {
      problem: "Early detection of ovarian cancer before stage III",
      population: "Asymptomatic women aged 45-65, average risk",
      constraints: "Acceptable for routine screening, sensitivity > 80%, low false-positive rate",
      geography: "Global, primary-care settings",
    },
  },
  {
    id: "maternal",
    title: "Maternal Health Monitoring",
    blurb: "Continuous risk monitoring for hypertensive disorders of pregnancy.",
    inputs: {
      problem: "Continuous monitoring for preeclampsia and other hypertensive disorders of pregnancy",
      population: "Pregnant women in second and third trimester",
      constraints: "Wearable / home-use, low literacy friendly, must integrate with ANC workflows",
      geography: "Sub-Saharan Africa and South Asia",
    },
  },
  {
    id: "tb",
    title: "Tuberculosis Screening",
    blurb: "Mass community TB screening with high sensitivity at low cost-per-test.",
    inputs: {
      problem: "High-throughput community tuberculosis screening",
      population: "Adults in TB-endemic regions, including HIV co-infected",
      constraints: "Cost per screen < $2, results within 30 min, robust to dust/heat",
      geography: "India, Indonesia, sub-Saharan Africa",
    },
  },
];

export const PROBLEM_LIBRARY = {
  "Infectious Disease": [
    { title: "Rapid dengue point-of-care diagnosis", inputs: { problem: "Rapid dengue NS1/IgM point-of-care diagnosis with serotyping", population: "Febrile patients in primary care", constraints: "POC, sensitivity > 90%, room temperature stable reagents", geography: "Southeast Asia, Latin America" } },
    { title: "Hospital-acquired infection detection", inputs: { problem: "Early detection of hospital-acquired infections from EHR signals", population: "Hospitalized adult inpatients", constraints: "Integrates with existing EHRs, low false-alert rate", geography: "Tertiary hospitals, India and US" } },
    { title: "Sepsis triage at PHC", inputs: { problem: "Early sepsis diagnosis in rural India", population: "Patients in primary health centres with suspected infection", constraints: "Low-cost, point-of-care, minimal lab infrastructure, intermittent power", geography: "India (Tier 3 cities and rural)" } },
    { title: "AMR surveillance", inputs: { problem: "Affordable antimicrobial resistance surveillance for district hospitals", population: "Hospital inpatients receiving empiric antibiotics", constraints: "Limited microbiology lab capacity, no sequencing on-site, budget < $50k per site", geography: "South / Southeast Asia" } },
  ],
  "Oncology": [
    { title: "Early ovarian cancer detection", inputs: { problem: "Early detection of ovarian cancer before stage III", population: "Asymptomatic women aged 45-65, average risk", constraints: "Acceptable for routine screening, sensitivity > 80%, low false-positive rate", geography: "Global, primary-care settings" } },
    { title: "Oral cancer screening", inputs: { problem: "Affordable oral cancer screening in tobacco-using populations", population: "Adults > 30 with tobacco/areca-nut exposure", constraints: "Field-deployable, < $5 per screen, < 5 min", geography: "South Asia" } },
    { title: "Pediatric brain tumor MRD", inputs: { problem: "Minimal residual disease monitoring in pediatric brain tumors", population: "Pediatric oncology patients post-resection", constraints: "Non-invasive or liquid biopsy, monthly cadence", geography: "Global pediatric oncology centers" } },
    { title: "Lung cancer LDCT triage", inputs: { problem: "AI triage of low-dose CT lung screening to reduce false positives", population: "High-risk smokers eligible for LDCT screening", constraints: "Integrates with PACS, <5% specificity loss", geography: "US, EU" } },
  ],
  "Maternal Health": [
    { title: "Preeclampsia monitoring", inputs: { problem: "Continuous monitoring for preeclampsia and other hypertensive disorders of pregnancy", population: "Pregnant women in second and third trimester", constraints: "Wearable / home-use, low literacy friendly, must integrate with ANC workflows", geography: "Sub-Saharan Africa and South Asia" } },
    { title: "Postpartum hemorrhage early warning", inputs: { problem: "Early warning system for postpartum hemorrhage in low-resource delivery settings", population: "Women within 24h postpartum at primary care facilities", constraints: "Minimal new hardware, integrates with partograph workflows", geography: "Sub-Saharan Africa, South Asia" } },
    { title: "Maternal mental health screening", inputs: { problem: "Routine screening for perinatal depression and anxiety", population: "Pregnant and postpartum women", constraints: "Stigma-aware, mobile-first, low literacy friendly", geography: "Global, primary care" } },
  ],
  "Public Health": [
    { title: "Wastewater pathogen surveillance", inputs: { problem: "Low-cost wastewater pathogen surveillance for outbreak early warning", population: "Urban and peri-urban communities", constraints: "No on-site sequencing, results within 48h, budget < $200/sample", geography: "Low- and middle-income countries" } },
    { title: "Air quality + respiratory disease", inputs: { problem: "Linking real-time air quality to respiratory exacerbations", population: "Children and elderly with asthma/COPD", constraints: "Wearable + ambient sensors, privacy-preserving", geography: "South Asian megacities" } },
    { title: "TB screening at scale", inputs: { problem: "High-throughput community tuberculosis screening", population: "Adults in TB-endemic regions, including HIV co-infected", constraints: "Cost per screen < $2, results within 30 min, robust to dust/heat", geography: "India, Indonesia, sub-Saharan Africa" } },
  ],
  "Diagnostics": [
    { title: "Point-of-care anemia screening", inputs: { problem: "Non-invasive anemia screening in pregnant women and children", population: "Pregnant women and children under 5", constraints: "Non-invasive, < $2/test, no consumables", geography: "Sub-Saharan Africa, South Asia" } },
    { title: "Smartphone otoscopy", inputs: { problem: "Smartphone-based otoscopy for childhood ear infections", population: "Children under 12 in primary care", constraints: "Works on mid-range smartphones, no extra hardware > $20", geography: "Global, primary care" } },
    { title: "Heart failure home monitoring", inputs: { problem: "Home monitoring for early heart failure decompensation", population: "Adults with NYHA II-III heart failure", constraints: "Passive sensing, integrates with PCP workflow", geography: "US, EU" } },
  ],
  "Chronic Disease": [
    { title: "Type 2 diabetes prediction", inputs: { problem: "Pre-symptomatic prediction of type 2 diabetes in young adults", population: "Adults 25-40 with BMI > 25", constraints: "Non-invasive or single fingerstick, monthly cadence", geography: "Global, employer wellness" } },
    { title: "CKD progression monitoring", inputs: { problem: "Affordable monitoring of chronic kidney disease progression", population: "Adults with CKD stage 2-4", constraints: "Home-use, integrates with nephrology workflows, < $10/test", geography: "South Asia, Latin America" } },
    { title: "Hypertension medication adherence", inputs: { problem: "Improving hypertension medication adherence in rural populations", population: "Hypertensive adults in rural communities", constraints: "No smartphone required, integrates with CHW workflows", geography: "Sub-Saharan Africa, South Asia" } },
  ],
};

// ---------------------------------------------------------------------------
// Tiny fetch helpers + in-memory cache (saves repeat calls when navigating tabs)
// ---------------------------------------------------------------------------
const cache = new Map();

export async function apiGet(path, { useCache = true } = {}) {
  if (useCache && cache.has(path)) return cache.get(path);
  const res = await fetch(`${API}${path}`);
  if (!res.ok) throw new Error(`GET ${path} ${res.status}`);
  const data = await res.json();
  cache.set(path, data);
  return data;
}

export async function apiPost(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path} ${res.status}`);
  return res.json();
}

export function invalidateCache(prefix = "") {
  for (const k of cache.keys()) if (k.startsWith(prefix)) cache.delete(k);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
export function useRunsList(refreshKey = 0) {
  const [data, setData] = useState({ runs: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    invalidateCache("/runs");
    apiGet("/runs", { useCache: false })
      .then((d) => { if (alive) setData(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshKey]);
  return { runs: data.runs || [], loading };
}

export function useProject(runId, { pollIfRunning = false } = {}) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    if (!runId) return;
    let alive = true;
    setLoading(true);
    apiGet(`/runs/${runId}`, { useCache: false })
      .then((d) => { if (alive) setDoc(d); })
      .catch(() => { if (alive) setDoc(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [runId, refreshKey]);

  useEffect(() => {
    if (!pollIfRunning || !doc || doc.status !== "running") return;
    const t = setInterval(() => setRefreshKey((k) => k + 1), 6000);
    return () => clearInterval(t);
  }, [pollIfRunning, doc]);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { doc, loading, refetch };
}

/**
 * Subscribe to a run's SSE stream. Returns live `agents` map keyed by event_name,
 * plus scores, founder_review, final_report, runStatus, tokenTail.
 */
export function useRunStream(runId, initialDoc = null) {
  const [agents, setAgents] = useState({});
  const [scores, setScores] = useState(null);
  const [founderReview, setFounderReview] = useState(null);
  const [finalReport, setFinalReport] = useState(null);
  const [runStatus, setRunStatus] = useState("");
  const [tokenTail, setTokenTail] = useState({ agent: "", text: "" });
  const [trace, setTrace] = useState([]);
  const [connectionState, setConnectionState] = useState("idle"); // idle | open | reconnecting | lost
  const esRef = useRef(null);
  const retriesRef = useRef(0);

  // Hydrate from doc immediately so completed runs show up without SSE.
  useEffect(() => {
    if (!initialDoc) return;
    const a = {};
    for (const [k, v] of Object.entries(initialDoc.outputs || {})) {
      a[k] = {
        output: v, started: true, completed: true,
        confidence: v?.confidence_score, lowConfidence: !!v?.low_confidence, retries: 0,
        durationMs: null, tokens: "",
      };
    }
    setAgents(a);
    if (initialDoc.scores) setScores(initialDoc.scores);
    if (initialDoc.founder_review) setFounderReview(initialDoc.founder_review);
    if (initialDoc.final_report) setFinalReport(initialDoc.final_report);
    if (initialDoc.status) setRunStatus(initialDoc.status);
    if (initialDoc.trace) setTrace(initialDoc.trace);
  }, [initialDoc]);

  useEffect(() => {
    if (!runId) return;
    if (initialDoc && initialDoc.status && initialDoc.status !== "running") return; // no SSE for terminal runs
    if (esRef.current) esRef.current.close();
    const es = new EventSource(`${API}/runs/${runId}/stream`);
    esRef.current = es;
    setRunStatus((s) => s || "running");

    const upd = (event, fn) =>
      setAgents((s) => ({ ...s, [event]: fn(s[event] || { tokens: "" }) }));

    es.addEventListener("agent_start", (e) => {
      const { agent } = JSON.parse(e.data);
      upd(agent, (a) => ({ ...a, started: true, retrying: false, tokens: "" }));
      setTokenTail({ agent, text: "" });
    });
    es.addEventListener("agent_retry", (e) => {
      const { agent } = JSON.parse(e.data);
      upd(agent, (a) => ({ ...a, retrying: true, retries: (a.retries || 0) + 1, tokens: "" }));
    });
    es.addEventListener("agent_repair", (e) => {
      const { agent } = JSON.parse(e.data);
      upd(agent, (a) => ({ ...a, repairing: true }));
    });
    es.addEventListener("agent_low_confidence", (e) => {
      const { agent } = JSON.parse(e.data);
      upd(agent, (a) => ({ ...a, lowConfidence: true }));
    });
    es.addEventListener("token", (e) => {
      const { agent, token } = JSON.parse(e.data);
      setAgents((s) => {
        const prev = s[agent] || { tokens: "" };
        const next = { ...prev, tokens: (prev.tokens || "") + token };
        return { ...s, [agent]: next };
      });
      setTokenTail((tt) => ({ agent, text: (tt.agent === agent ? tt.text : "") + token }));
    });
    es.addEventListener("agent_complete", (e) => {
      const { agent, output } = JSON.parse(e.data);
      upd(agent, (a) => ({ ...a, output, completed: true, retrying: false, repairing: false, confidence: output?.confidence_score }));
    });
    es.addEventListener("scores", (e) => setScores(JSON.parse(e.data)));
    es.addEventListener("founder_review", (e) => setFounderReview(JSON.parse(e.data)));
    es.addEventListener("final_report", (e) => setFinalReport(JSON.parse(e.data)));
    es.addEventListener("run_complete", () => { setRunStatus("complete"); setConnectionState("idle"); es.close(); });
    es.addEventListener("run_failed", () => { setRunStatus("failed"); setConnectionState("idle"); es.close(); });
    es.addEventListener("run_cancelled", () => { setRunStatus("cancelled"); setConnectionState("idle"); es.close(); });
    es.onopen = () => {
      retriesRef.current = 0;
      setConnectionState("open");
    };
    es.onerror = () => {
      // Native EventSource auto-reconnects unless readyState === CLOSED.
      if (es.readyState === EventSource.CLOSED) return;
      retriesRef.current += 1;
      setConnectionState(retriesRef.current >= 3 ? "lost" : "reconnecting");
    };
    return () => { es.close(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, initialDoc?.status]);

  return { agents, scores, founderReview, finalReport, runStatus, tokenTail, trace, setTrace, connectionState };
}

export function formatUSD(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Math.round(n)}`;
}

export function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export const SEVERITY = {
  Low: { color: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/30" },
  Medium: { color: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/30" },
  High: { color: "text-orange-300", bg: "bg-orange-500/15", border: "border-orange-500/30" },
  Critical: { color: "text-rose-300", bg: "bg-rose-500/20", border: "border-rose-500/40" },
};

export function scoreColor(score, { invertHigh = false } = {}) {
  if (score == null) return "text-muted-foreground";
  const s = invertHigh ? 100 - score : score;
  if (s >= 70) return "text-emerald-300";
  if (s >= 50) return "text-amber-300";
  return "text-rose-300";
}

export function scoreStroke(score, { invertHigh = false } = {}) {
  if (score == null) return "hsl(215 14% 50%)";
  const s = invertHigh ? 100 - score : score;
  if (s >= 70) return "hsl(155 65% 50%)";
  if (s >= 50) return "hsl(40 95% 60%)";
  return "hsl(0 75% 60%)";
}
